/**
 * Utility functions for handling biomarker data with gender-specific ranges
 */

/**
 * Format a biomarker value for display: values less than 1 that start with "0."
 * are shown without the leading zero (e.g. 0.5 → ".5", 0.123 → ".123").
 * @param {number|string|null|undefined} value - Raw value from result or range
 * @returns {string} - Formatted string for display
 */
export function formatBiomarkerValueForDisplay(value) {
  if (value === null || value === undefined) return '';
  const str = String(value).trim();
  if (str.startsWith('0.') && str.length > 2) {
    return '.' + str.slice(2);
  }
  return str;
}

/**
 * Get clinical ranges for a biomarker based on user gender
 * @param {Object} biomarker - Biomarker object from database
 * @param {string} userGender - User's gender ('male', 'female', or other)
 * @returns {Object} - Object with clinical_low and clinical_high
 */
export function getClinicalRanges(biomarker, userGender) {
  if (!biomarker) {
    return { clinical_low: null, clinical_high: null };
  }

  // Normalize gender string to lowercase
  const gender = (userGender || '').toLowerCase();

  // Pick gender-specific low/high - allow partial ranges (e.g. high-only for biomarkers like eGFR)
  const pickGenderRanges = (lowField, highField) => ({
    clinical_low: biomarker[lowField] ?? null,
    clinical_high: biomarker[highField] ?? null
  });

  // Return gender-specific ranges when at least one bound is available
  if (gender === 'male' || gender === 'm') {
    if (biomarker.clinical_low_male != null || biomarker.clinical_high_male != null) {
      return pickGenderRanges('clinical_low_male', 'clinical_high_male');
    }
  } else if (gender === 'female' || gender === 'f') {
    if (biomarker.clinical_low_female != null || biomarker.clinical_high_female != null) {
      return pickGenderRanges('clinical_low_female', 'clinical_high_female');
    }
  }

  // For other genders or if specific ranges aren't available,
  // try male ranges, then female, then direct clinical_low/clinical_high (allow partial)
  if (biomarker.clinical_low_male != null || biomarker.clinical_high_male != null) {
    return pickGenderRanges('clinical_low_male', 'clinical_high_male');
  }
  if (biomarker.clinical_low_female != null || biomarker.clinical_high_female != null) {
    return pickGenderRanges('clinical_low_female', 'clinical_high_female');
  }

  // Fallback to direct clinical_low/clinical_high (e.g. enriched biomarkers)
  return {
    clinical_low: biomarker.clinical_low ?? null,
    clinical_high: biomarker.clinical_high ?? null
  };
}

/**
 * Create a biomarker object with clinical ranges based on user gender
 * This is useful for components that expect clinical_low/clinical_high directly
 * @param {Object} biomarker - Biomarker object from database
 * @param {string} userGender - User's gender
 * @returns {Object} - Biomarker with clinical_low and clinical_high added
 */
export function enrichBiomarkerWithRanges(biomarker, userGender) {
  if (!biomarker) return null;

  const ranges = getClinicalRanges(biomarker, userGender);
  
  return {
    ...biomarker,
    clinical_low: ranges.clinical_low,
    clinical_high: ranges.clinical_high
  };
}

/**
 * Enrich an array of biomarkers with appropriate ranges
 * @param {Array} biomarkers - Array of biomarker objects
 * @param {string} userGender - User's gender
 * @returns {Array} - Array of enriched biomarkers
 */
export function enrichBiomarkersWithRanges(biomarkers, userGender) {
  if (!Array.isArray(biomarkers)) return [];
  
  return biomarkers.map(biomarker => enrichBiomarkerWithRanges(biomarker, userGender));
}

/**
 * Get the appropriate SELECT fields for biomarker queries
 * @returns {string} - Comma-separated field list for Supabase queries
 */
export function getBiomarkerSelectFields() {
  return 'id, name, category, unit, description, clinical_low_male, clinical_high_male, clinical_low_female, clinical_high_female, range_source, description_source';
}

/**
 * Determine biomarker status based on value and ranges
 * @param {number} value - The biomarker value
 * @param {Object} biomarker - Biomarker object with clinical ranges
 * @param {string} userGender - User's gender
 * @returns {string} - 'critical', 'warning', 'normal', or 'unknown'
 */
export function getBiomarkerStatus(value, biomarker, userGender) {
  const ranges = getClinicalRanges(biomarker, userGender);
  
  if (ranges.clinical_low === null || ranges.clinical_high === null) {
    return 'unknown';
  }

  if (value < ranges.clinical_low || value > ranges.clinical_high) {
    return 'critical';
  }

  return 'normal';
}

/**
 * Calculate extended range for visualization (clinical range + 20% extension)
 * @param {Object} biomarker - Biomarker object
 * @param {string} userGender - User's gender
 * @param {number|null} userValue - Optional user value for handling null low ranges
 * @returns {Object} - { min, max, range }
 */
export function getExtendedRange(biomarker, userGender, userValue = null) {
  const ranges = getClinicalRanges(biomarker, userGender);
  const { clinical_low, clinical_high } = ranges;
  
  // If both are null, use default range
  if (clinical_low === null && clinical_high === null) {
    if (userValue !== null) {
      // Use 0 as min and userValue * 1.5 as max (with 50% extension)
      return { min: 0, max: userValue * 1.5, range: userValue * 1.5 };
    }
    return { min: 0, max: 100, range: 100 };
  }
  
  // If low is null but high is not, use 0 as min (for visualization)
  if (clinical_low === null && clinical_high !== null) {
    const extension = clinical_high * 0.2;
    const max = clinical_high + extension;
    return { min: 0, max: max, range: max };
  }
  
  // If high is null but low is not
  if (clinical_low !== null && clinical_high === null) {
    const extension = clinical_low * 0.2;
    const min = Math.max(0, clinical_low - extension);
    const max = userValue !== null ? Math.max(userValue * 1.2, clinical_low * 1.5) : clinical_low * 1.5;
    return { min, max, range: max - min };
  }
  
  // Both are not null (normal case)
  const clinicalRange = clinical_high - clinical_low;
  const extension = clinicalRange * 0.2;
  const min = clinical_low - extension;
  const max = clinical_high + extension;
  
  return { min, max, range: max - min };
}

/**
 * Calculate user value position as percentage within extended range
 * @param {Object} biomarker - Biomarker object
 * @param {Object} result - Result object with value
 * @param {string} userGender - User's gender
 * @returns {number|null} - Position as percentage (0-100) or null
 */
export function getUserValuePosition(biomarker, result, userGender) {
  if (!result || !biomarker) return null;
  
  const value = parseFloat(result.value);
  const ranges = getClinicalRanges(biomarker, userGender);
  
  // If both ranges are null, can't calculate position
  if (ranges.clinical_low === null && ranges.clinical_high === null) {
    return null;
  }
  
  // Pass user value to getExtendedRange for proper calculation when low is null
  const extendedRange = getExtendedRange(biomarker, userGender, value);
  const position = ((value - extendedRange.min) / extendedRange.range) * 100;
  
  return Math.max(0, Math.min(100, position));
}

/**
 * Calculate clinical range position within extended range
 * @param {Object} biomarker - Biomarker object
 * @param {string} userGender - User's gender
 * @param {number|null} userValue - Optional user value for handling null low ranges
 * @returns {Object} - { start, end } as percentages
 */
export function getClinicalRangePosition(biomarker, userGender, userValue = null) {
  const ranges = getClinicalRanges(biomarker, userGender);
  const { clinical_low, clinical_high } = ranges;
  
  // If both are null, return full range
  if (clinical_low === null && clinical_high === null) {
    return { start: 0, end: 100 };
  }
  
  const extendedRange = getExtendedRange(biomarker, userGender, userValue);
  
  // If low is null, start from 0
  const start = clinical_low === null ? 0 : ((clinical_low - extendedRange.min) / extendedRange.range) * 100;
  // If high is null, end at 100
  const end = clinical_high === null ? 100 : ((clinical_high - extendedRange.min) / extendedRange.range) * 100;
  
  return { start, end };
}

/**
 * Shorten URL for display (returns hostname without www.)
 * Matches the logic used in BiohackInfoCard
 * @param {string} url - The URL to shorten
 * @returns {string} - The shortened URL (e.g., "example.com" from "https://www.example.com/path")
 */
export function shortenUrl(url) {
  if (!url) return '';
  // Trim whitespace to handle URLs with leading/trailing spaces
  const trimmedUrl = url.trim();
  try {
    const urlObj = new URL(trimmedUrl.startsWith('http') ? trimmedUrl : `https://${trimmedUrl}`);
    let hostname = urlObj.hostname;
    // Remove 'www.' if present
    if (hostname.startsWith('www.')) {
      hostname = hostname.substring(4);
    }
    return hostname;
  } catch (error) {
    // If URL parsing fails, try simple extraction
    let urlStr = trimmedUrl.replace(/^https?:\/\//, '').replace(/^www\./, '');
    return urlStr.split('/')[0];
  }
}

/**
 * Extract website name from a URL
 * Extracts the domain name (after www. and before the TLD)
 * @param {string} url - The URL to extract the website name from
 * @returns {string} - The website name (e.g., "example" from "www.example.com")
 */
export function extractWebsiteName(url) {
  if (!url) return '';
  
  try {
    // Handle URLs that might not have protocol
    let urlToParse = url;
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      urlToParse = 'https://' + url;
    }
    
    const urlObj = new URL(urlToParse);
    let hostname = urlObj.hostname;
    
    // Remove 'www.' if present
    if (hostname.startsWith('www.')) {
      hostname = hostname.substring(4);
    }
    
    // Handle common multi-part TLDs (e.g., .co.uk, .com.au)
    const multiPartTlds = ['.co.uk', '.com.au', '.co.nz', '.co.za', '.com.br', '.com.mx'];
    let domain = hostname;
    
    for (const tld of multiPartTlds) {
      if (hostname.endsWith(tld)) {
        domain = hostname.slice(0, -tld.length);
        break;
      }
    }
    
    // If no multi-part TLD matched, extract domain before the last dot
    if (domain === hostname) {
      const parts = hostname.split('.');
      if (parts.length > 1) {
        // Remove the last part (TLD) and join the rest
        domain = parts.slice(0, -1).join('.');
      }
    }
    
    return domain || hostname;
  } catch (error) {
    // If URL parsing fails, try a simple regex approach
    let urlStr = url;
    if (urlStr.startsWith('www.')) {
      urlStr = urlStr.substring(4);
    }
    
    // Remove protocol if present
    urlStr = urlStr.replace(/^https?:\/\//, '');
    
    // Remove path and query
    urlStr = urlStr.split('/')[0];
    
    // Handle multi-part TLDs
    const multiPartTlds = ['.co.uk', '.com.au', '.co.nz', '.co.za', '.com.br', '.com.mx'];
    for (const tld of multiPartTlds) {
      if (urlStr.endsWith(tld)) {
        return urlStr.slice(0, -tld.length);
      }
    }
    
    // Extract domain before TLD
    const parts = urlStr.split('.');
    if (parts.length > 1) {
      return parts.slice(0, -1).join('.');
    }
    
    return urlStr;
  }
}